/**
 * Supplier-safe view of the RFQ activity timeline.
 *
 * The customer sees the full log. A supplier must only see entries about
 * the RFQ itself or about their own company — never "Response submitted by
 * <competitor>" or who the RFQ was awarded to. The backend redacts too;
 * this filter is the UI's own guarantee and does not rely on any field
 * being present.
 */
import type { RFQActivityLog } from '../../lib/api';

/** Actions that describe the RFQ as a whole and are safe for every invitee. */
const RFQ_WIDE_ACTIONS = new Set(['created', 'sent', 'awarded', 'cancelled', 'updated', 'deadline_extended']);

function metaNumber(meta: Record<string, unknown> | undefined, key: string): number | null {
  const v = meta?.[key];
  return typeof v === 'number' ? v : null;
}

function metaNumberList(meta: Record<string, unknown> | undefined, key: string): number[] {
  const v = meta?.[key];
  return Array.isArray(v) ? v.filter((x): x is number => typeof x === 'number') : [];
}

export interface SupplierActivityOptions {
  /** The supplier's company id */
  companyId: number;
  /** Ids of query threads the supplier can see (their own) */
  ownQueryIds?: number[];
  /** Whether this supplier won the award (drives the awarded wording) */
  isWinner?: boolean;
}

/**
 * Filter + redact activity entries for a supplier viewer.
 *
 * @returns Entries the supplier may see, with competitor-revealing
 *   descriptions replaced by neutral wording.
 */
export function filterActivityForSupplier(
  logs: RFQActivityLog[],
  { companyId, ownQueryIds = [], isWinner = false }: SupplierActivityOptions,
): RFQActivityLog[] {
  const out: RFQActivityLog[] = [];
  for (const log of logs) {
    const meta = log.metadata ?? {};
    const supplierId = metaNumber(meta, 'supplier_id');
    const queryId = metaNumber(meta, 'query_id');
    const isOwn = supplierId === companyId || (queryId !== null && ownQueryIds.includes(queryId));

    // Award / send are RFQ-wide events every invitee may see, but their
    // description names suppliers — neutralise it unless it is about them.
    if (log.action === 'awarded') {
      if (isWinner || supplierId === companyId) {
        out.push(log);
      } else {
        out.push({ ...log, description: 'RFQ awarded to another supplier', performed_by_name: '', metadata: {} });
      }
      continue;
    }
    if (log.action === 'sent') {
      const ids = metaNumberList(meta, 'supplier_ids');
      if (ids.length && !ids.includes(companyId)) continue;
      out.push({ ...log, description: 'RFQ sent to suppliers', performed_by_name: '', metadata: {} });
      continue;
    }

    if (isOwn) {
      out.push(log);
      continue;
    }
    if (supplierId !== null || queryId !== null) {
      // Another supplier's entry — hide.
      continue;
    }
    if (!RFQ_WIDE_ACTIONS.has(log.action)) continue;
    out.push({ ...log, performed_by_name: '', metadata: {} });
  }
  return out;
}
